import type { ComponentType, ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { AUTH_ROUTES } from "./nav-config";
import { useStoreSettings } from "@/lib/admin/settings";
import { useSection } from "@/lib/cms/content";

/** Operator-supplied path — routed through TanStack Link when internal. */
const AnyLink = Link as unknown as ComponentType<{
  to: string;
  className?: string;
  children: ReactNode;
}>;

function CmsLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  if (!href.startsWith("/")) {
    return (
      <a href={href || "#"} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <AnyLink to={href} className={className}>
      {children}
    </AnyLink>
  );
}

/** Parses "Label | /path" lines from the CMS into link objects. */
function parseLinks(raw: string): Array<{ label: string; to: string }> {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, to] = line.split("|").map((p) => p.trim());
      return { label: label ?? "", to: to ?? "#" };
    })
    .filter((l) => l.label);
}

export function Footer() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const settings = useStoreSettings();
  const cms = useSection("home", "footer");
  const isAuthRoute = AUTH_ROUTES.has(pathname);
  if (isAuthRoute) return null;
  const socials = Object.entries(settings.social).filter(([, v]) => !!v) as [string, string][];

  const columns = cms.list("columns").map((c) => ({
    heading: c.heading ?? "",
    links: parseLinks(c.links ?? ""),
  }));
  const legalLinks = parseLinks(cms.s("legalLinks"));
  const brandName = cms.s("brandName") || settings.storeName;
  const tagline = cms.s("tagline") || settings.footer.tagline;
  const copyright = cms.s("copyright") || settings.footer.copyright;
  const logo = cms.s("logo");
  // Footer email falls back to the Support Email in Settings → Contact.
  const footerEmail = cms.s("email") || settings.contact.email;
  const contactLines = [cms.s("address"), cms.s("phone")].filter(Boolean);

  return (
    <footer
      className="mt-24 border-t border-black/10 bg-white"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            {logo ? (
              <img src={logo} alt={brandName} className="h-8 w-auto" />
            ) : (
              <div
                className="text-lg font-medium uppercase tracking-[0.4em] text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {brandName}
              </div>
            )}
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-black/60">{tagline}</p>
            <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
            {(contactLines.length > 0 || footerEmail) && (
              <div className="mt-6 space-y-1 text-sm text-black/60">
                {cms.s("contactHeading") && (
                  <h3 className="text-[11px] font-medium uppercase tracking-[0.24em] text-black">
                    {cms.s("contactHeading")}
                  </h3>
                )}
                {cms.s("address") && <p className="whitespace-pre-line">{cms.s("address")}</p>}
                {footerEmail && (
                  <p>
                    <a href={`mailto:${footerEmail}`} className="transition hover:text-[#C6A15B]">{footerEmail}</a>
                  </p>
                )}
                {cms.s("phone") && <p className="whitespace-pre-line">{cms.s("phone")}</p>}
              </div>
            )}
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3">
                {socials.map(([k, v]) => (
                  <a key={k} href={v} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] font-semibold uppercase tracking-[0.24em] text-black/60 transition hover:text-[#C6A15B]">
                    {k}
                  </a>
                ))}
              </div>
            )}
          </div>
          {columns.map((col) => (
            <div key={col.heading}>
              <h3 className="text-[11px] font-medium uppercase tracking-[0.24em] text-black">
                {col.heading}
              </h3>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <CmsLink href={l.to} className="text-sm text-black/60 transition hover:text-[#C6A15B]">
                      {l.label}
                    </CmsLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-black/10 pt-6 sm:flex-row sm:items-center">
          <div className="text-xs tracking-wide text-black/50">
            <p>{copyright.replace("© ", `© ${new Date().getFullYear()} `).replace(/^© \d{4} © \d{4} /, `© ${new Date().getFullYear()} `)}</p>
            {(settings.legal.gstin || settings.legal.cin) && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-black/40">
                {[settings.legal.gstin && `GSTIN ${settings.legal.gstin}`, settings.legal.cin && `CIN ${settings.legal.cin}`].filter(Boolean).join(" · ")}
              </p>
            )}
            {cms.s("bottomText") && (
              <p className="mt-1 text-[10px] tracking-wide text-black/40">{cms.s("bottomText")}</p>
            )}
          </div>
          <div className="flex gap-6 text-xs uppercase tracking-[0.2em] text-black/50">
            {legalLinks.map((l) => (
              <CmsLink key={l.to + l.label} href={l.to} className="hover:text-[#C6A15B]">
                {l.label}
              </CmsLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
